import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import Clients from "../components/Clients";
import { db } from '../firebaseConfig';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';

function Home() {
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [testimonialsData, setTestimonialsData] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [featuredProduct, setFeaturedProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      setLoadingTestimonials(true);
      try {
        const snapshot = await getDocs(collection(db, 'testimonials'));
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTestimonialsData(data);
      } catch (err) {
        setTestimonialsData([]);
      }
      setLoadingTestimonials(false);
    };
    fetchTestimonials();
  }, []);

  useEffect(() => {
    const fetchFeaturedProduct = async () => {
      try {
        // Query for the first visible product
        const q = query(
          collection(db, "projects"),
          where("collection", "==", "products"),
          where("visible", "==", true),
          limit(1)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          const product = { id: doc.id, ...doc.data() };
          setFeaturedProduct(product);
        }
      } catch (err) {
        console.error('Error fetching featured product: ', err);
      } finally {
        setLoadingProduct(false);
      }
    };

    fetchFeaturedProduct();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex(prev => (prev + 1) % testimonialsData.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonialsData.length]);

  useEffect(() => {
    const handleThemeChange = () => {
      setTheme(localStorage.getItem('theme') || 'light');
    };
    window.addEventListener('themechange', handleThemeChange);
    window.addEventListener('storage', handleThemeChange);
    return () => {
      window.removeEventListener('themechange', handleThemeChange);
      window.removeEventListener('storage', handleThemeChange);
    };
  }, []);

  const renderFeaturedProduct = () => {
    if (loadingProduct) {
      return (
        <div className="flex">
          <div>
            <h2>Loading...</h2>
            <p>Loading featured product...</p>
          </div>
        </div>
      );
    }

    if (!featuredProduct) {
      return (
        <div className="flex">
          <div>
            <h2>We're working on it</h2>
            <p>No featured product found</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex">
        <div>
          <h2>{featuredProduct.name}</h2>
          <p>{featuredProduct.description}</p>
          {featuredProduct.platforms && featuredProduct.platforms.length > 0 && (
            <div className="btn-container">
              {featuredProduct.platforms.map((platform, index) => (
                <span key={index} className="btn">{platform}</span>
              ))}
            </div>
          )}
          <Link to={`/products/${featuredProduct.id}`}>
            View Product <img src="media/assets/arrow02.png" alt="arrow" />
          </Link>
        </div>
        <div className="flex-img">
          <img
            src={featuredProduct.image && featuredProduct.image.url ? featuredProduct.image.url : featuredProduct.image}
            alt={featuredProduct.name}
          />
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="home01">
        <div>
          <p>At FutApp, we are a dynamic and innovative software development company committed to transforming ideas into reality. Our journey began in 2023, when a group of passionate tech enthusiasts decided to combine their expertise and vision to create solutions that drive digital transformation. From our humble beginnings, we have grown into a dedicated team of skilled developers, designers, and project managers who work tirelessly to deliver top-notch software solutions.</p>
          <Link to="/about">Learn More <img src="media/assets/arrow02.png" alt="arrow" /></Link>
        </div>
        <div className="home-banner">
          <h1>Innovative Solutions For The Future</h1>
          <p>Innovate, Integrate, Inspire: Your Software Solutions Partner.</p>
          <Link to="/contact">
            <button className="btn">Let's Talk!</button>
          </Link>
        </div>
      </div>

      <Clients />

      <div className="home02">
        <h1>Featured Products</h1>
        <p>Unlock the potential of tomorrow with FutApp's cutting-edge solutions! Our platform offers tailored, ready-to-use tools designed to streamline your processes, enhance productivity, and spark innovation. Whether you're seeking smart integrations, futuristic designs, or advanced technology applications, FutApp empowers you to achieve your goals effortlessly. <br />
          Experience the future today!</p>
      </div>

      {renderFeaturedProduct()}

      <div className="home02">
        <Link to="/products"><button className="btn">View All Products</button></Link>
      </div>

      <div className="home02">
        <h1>
          Development Process
        </h1>

        <p>Creating an application or website involves multiple stages, blending creativity, technical expertise, and project management. Here's a general overview of the process:</p>

        <div className="process-container">
          <div className="row">
            <div className="card">
              Concept & Planning
            </div>
            <div className="card">
              Development
            </div>
            <div className="card">
              Launch
            </div>
          </div>

          <div className="row">
            <div className="card">
              Design & Visuals
            </div>
            <div className="card">
              Testing
            </div>
          </div>
          <div className="process">
          </div>
        </div>
        <div className="process-container02">
          <img src="media/Home/steps.png" alt="Development Process" className="light" />
          <img src="media/Home/steps-dark.png" alt="Development Process" className="dark" />
        </div>
      </div>

      <h1 className="section-title">Testimonials</h1>
      {loadingTestimonials ? (
        <div className="loading">Loading testimonials...</div>
      ) : (
        <div className="testimonials">
          <div className="testimonial-slider-wrapper">
            <div
              className="testimonial-content testimonial-slider"
              style={{
                transform: `translateX(-${currentTestimonialIndex * 100}%)`,
                transition: 'transform 0.6s cubic-bezier(.77,0,.18,1)'
              }}
            >
              {testimonialsData.map((t, idx) => (
                <div
                  className="testimonial-item"
                  key={t.id || idx}
                  style={{ minWidth: '100%', boxSizing: 'border-box', display: 'flex' }}
                >
                  <div className="testimonial-left">
                    <div className="testimonial-logo">
                      <img
                        src={
                          theme === 'dark'
                            ? t.logoDark?.url || t.logoDark || t.logoLight?.url || t.logoLight
                            : t.logoLight?.url || t.logoLight
                        }
                        alt="Testimonial Logo"
                      />
                    </div>
                    <div className="testimonial-text">
                      <p className="testimonial-title">{t.title}</p>
                      <p className="testimonial-name">{t.name}</p>
                      <p
                        className="testimonial-quote"
                        dangerouslySetInnerHTML={{ __html: t.quote.replace(/\n/g, '<br />') }}
                      />
                    </div>
                  </div>
                  <div className="testimonial-right">
                    <img src={t.image?.url || t.image} alt="FutApp on phones" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="testimonial-pagination">
            {testimonialsData.map((_, idx) => (
              <span
                key={idx}
                className={`dot${idx === currentTestimonialIndex ? ' active' : ''}`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                style={{ cursor: 'pointer' }}
              ></span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;